import React, { useState, useEffect, useRef } from 'react';
import { useProjectStore } from '../../store/projectStore';
import { PlacedEquipment, Cable, Project } from '../../types';
import { Download, FileText, Settings, User, Building, Wifi, Cable as CableIcon, Server, Printer } from 'lucide-react';
import DesignSelector from '../Designer/DesignSelector';
import { uniqueCablesById } from '../../utils/cableRuns';

interface CustomerDetails {
  companyName: string;
  contactPerson: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  email: string;
  phone: string;
  website?: string;
  projectManager: string;
  projectDate: string;
  projectDescription: string;
}

const ReportView: React.FC = () => {
  const { currentProject, updateProjectCustomer, getCurrentDesign } = useProjectStore();
  const design = getCurrentDesign();
  const designCables = uniqueCablesById(design?.cables);
  const reportRef = useRef<HTMLDivElement>(null);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>({
    companyName: '',
    contactPerson: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    email: '',
    phone: '',
    website: '',
    projectManager: '',
    projectDate: new Date().toLocaleDateString(),
    projectDescription: '',
  });

  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');

  // Auto-update when project changes
  useEffect(() => {
    if (currentProject) {
      setCustomerDetails(prev => ({
        ...prev,
        companyName: currentProject.customer.name || prev.companyName,
        address: currentProject.customer.address || prev.address,
        email: currentProject.customer.email || prev.email,
        phone: currentProject.customer.phone || prev.phone,
      }));
    }
  }, [currentProject]);

  if (!currentProject || !design) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FileText size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Collection Selected</h2>
          <p className="text-gray-500">Please create or open a collection to generate reports</p>
        </div>
      </div>
    );
  }

  // Calculate statistics based on the current design (adapter over project data)
  const equipmentCount = design ? design.equipment.length : 0;
  const existingEquipmentCount = design ? design.equipment.filter(eq => eq.isExisting).length : 0;
  const newEquipmentCount = equipmentCount - existingEquipmentCount;
  const cableCount = designCables.length;
  const existingCableCount = designCables.filter(c => (c as any).isExisting).length;
  const newCableCount = cableCount - existingCableCount;
  const rackCount = design ? design.racks.length : 0;
  
  const equipmentByType = (design ? design.equipment : []).reduce((acc, eq) => {
    acc[eq.equipment.type] = (acc[eq.equipment.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const cableByType = designCables.reduce((acc, cable) => {
    acc[cable.type] = (acc[cable.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const totalPorts = (design ? design.equipment : []).reduce((total, eq) => {
    return total + (eq.equipment.specifications?.ports || 0);
  }, 0);

  const usedPorts = (design ? design.cables.length : 0) * 2; // Each cable uses 2 ports

  // PDF Export function
  const handleExportPDF = () => {
    if (!reportRef.current) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Get the report content
    const reportContent = reportRef.current.innerHTML;
    
    // Create the print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Network Infrastructure Report - ${currentProject.name}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.4;
              color: #333;
              background: white;
            }
            
            .a4-page { 
              background: #fff; 
              box-shadow: 0 2px 10px rgba(0,0,0,0.08); 
              border-radius: 8px; 
              margin: 12px auto; 
            }
            
            .a4-content { 
              padding: 12mm; 
            }
            
            .a4-portrait { 
              width: 210mm; 
              min-height: 297mm; 
            }
            
            .a4-landscape { 
              width: 297mm; 
              min-height: 210mm; 
            }
            
            .avoid-break { 
              page-break-inside: avoid; 
            }
            
            @media print {
              .a4-page { 
                box-shadow: none; 
                border-radius: 0; 
                margin: 0; 
                page-break-inside: avoid;
              }
              
              .a4-content { 
                padding: 12mm; 
              }
              
              @page { 
                size: A4 ${orientation}; 
                margin: 12mm; 
              }
              
              body { 
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact; 
              }
            }
            
            /* Hide print controls */
            .print-controls {
              display: none;
            }
            
            @media print {
              .print-controls {
                display: none !important;
              }
            }
            
            /* Ensure proper styling */
            table {
              border-collapse: collapse;
              width: 100%;
            }
            
            th, td {
              border: 1px solid #d1d5db;
              padding: 4px 8px;
              text-align: left;
            }
            
            th {
              background-color: #f9fafb;
              font-weight: 600;
            }
            
            .bg-gray-50 {
              background-color: #f9fafb;
            }
            
            .bg-white {
              background-color: white;
            }
            
            .text-gray-900 {
              color: #111827;
            }
            
            .text-gray-700 {
              color: #374151;
            }
            
            .text-gray-600 {
              color: #4b5563;
            }
            
            .text-gray-500 {
              color: #6b7280;
            }
            
            .font-semibold {
              font-weight: 600;
            }
            
            .font-medium {
              font-weight: 500;
            }
            
            .font-bold {
              font-weight: 700;
            }
            
            .text-xs {
              font-size: 0.75rem;
            }
            
            .text-sm {
              font-size: 0.875rem;
            }
            
            .text-base {
              font-size: 1rem;
            }
            
            .text-lg {
              font-size: 1.125rem;
            }
            
            .text-xl {
              font-size: 1.25rem;
            }
            
            .text-2xl {
              font-size: 1.5rem;
            }
            
            .mb-1 { margin-bottom: 0.25rem; }
            .mb-2 { margin-bottom: 0.5rem; }
            .mb-3 { margin-bottom: 0.75rem; }
            .mb-4 { margin-bottom: 1rem; }
            .mb-6 { margin-bottom: 1.5rem; }
            .mb-8 { margin-bottom: 2rem; }
            
            .mt-8 { margin-top: 2rem; }
            
            .p-3 { padding: 0.75rem; }
            .p-6 { padding: 1.5rem; }
            
            .pb-6 { padding-bottom: 1.5rem; }
            .pt-4 { padding-top: 1rem; }
            
            .px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
            .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
            
            .border-b { border-bottom: 1px solid #d1d5db; }
            .border-t { border-top: 1px solid #d1d5db; }
            
            .rounded-lg { border-radius: 0.5rem; }
            
            .flex { display: flex; }
            .items-center { align-items: center; }
            .items-start { align-items: flex-start; }
            .justify-between { justify-content: space-between; }
            .justify-center { justify-content: center; }
            
            .space-x-2 > * + * { margin-left: 0.5rem; }
            .space-x-3 > * + * { margin-left: 0.75rem; }
            .space-x-4 > * + * { margin-left: 1rem; }
            
            .space-y-1 > * + * { margin-top: 0.25rem; }
            .space-y-2 > * + * { margin-top: 0.5rem; }
            .space-y-3 > * + * { margin-top: 0.75rem; }
            .space-y-6 > * + * { margin-top: 1.5rem; }
            
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
            .gap-3 { gap: 0.75rem; }
            .gap-4 { gap: 1rem; }
            
            .overflow-x-auto { overflow-x: auto; }
            
            .w-full { width: 100%; }
            .w-3 { width: 0.75rem; }
            .h-3 { height: 0.75rem; }
            
            .bg-blue-500 { background-color: #3b82f6; }
            .bg-gray-50 { background-color: #f9fafb; }
            .bg-white { background-color: white; }
            
            .rounded-full { border-radius: 9999px; }
            .rounded { border-radius: 0.25rem; }
            
            .text-blue-600 { color: #2563eb; }
            .text-blue-800 { color: #1e40af; }
            .text-green-800 { color: #166534; }
            
            .bg-blue-100 { background-color: #dbeafe; }
            .bg-green-100 { background-color: #dcfce7; }
            
            .inline-flex { display: inline-flex; }
            .px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
            .py-0\.5 { padding-top: 0.125rem; padding-bottom: 0.125rem; }
            
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            
            .capitalize { text-transform: capitalize; }
            
            .mx-auto { margin-left: auto; margin-right: auto; }
            .mx-2 { margin-left: 0.5rem; margin-right: 0.5rem; }
            
            .ml-1 { margin-left: 0.25rem; }
            .ml-2 { margin-left: 0.5rem; }
            .mr-2 { margin-right: 0.5rem; }
            
            .truncate { 
              overflow: hidden; 
              text-overflow: ellipsis; 
              white-space: nowrap; 
            }
            
            .max-w-\[200px\] { max-width: 200px; }
            
            /* Hide icons in print */
            svg {
              display: none;
            }
            
            /* Ensure proper spacing for icon placeholders */
            .flex.items-center svg + * {
              margin-left: 0;
            }
          </style>
        </head>
        <body>
          ${reportContent}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <div className="flex-1 flex bg-gray-50">
      {/* Report Controls */}
      <div className="w-80 bg-white border-r border-gray-200 p-4">
        {/* Design Selector */}
        <DesignSelector />

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Report Settings</h3>
          
          <div className="space-y-3">
            <button
              onClick={handleExportPDF}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Printer size={16} />
              <span>Export to PDF</span>
            </button>
            
            <button
              onClick={() => setShowCustomerForm(!showCustomerForm)}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <User size={16} />
              <span>Customer Details</span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Page Layout */}
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Page Layout</h4>
            <div className="flex gap-2">
              <button
                onClick={() => setOrientation('portrait')}
                className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${orientation === 'portrait' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'}`}
                title="A4 Portrait"
              >
                A4 Portrait
              </button>
              <button
                onClick={() => setOrientation('landscape')}
                className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${orientation === 'landscape' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'}`}
                title="A4 Landscape"
              >
                A4 Landscape
              </button>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Project Statistics</h4>
              <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Equipment:</span>
                  <span className="font-medium">{equipmentCount} 
                    <span className="text-xs text-gray-500">(New {newEquipmentCount} • Existing {existingEquipmentCount})</span>
                  </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cables:</span>
                  <span className="font-medium">{cableCount} 
                    <span className="text-xs text-gray-500">(New {newCableCount} • Existing {existingCableCount})</span>
                  </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Racks:</span>
                <span className="font-medium">{rackCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Ports:</span>
                <span className="font-medium">{totalPorts}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Used Ports:</span>
                <span className="font-medium">{usedPorts}</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-2">Equipment by Type</h4>
            <div className="space-y-1 text-sm">
              {Object.entries(equipmentByType).map(([type, count]) => (
                <div key={type} className="flex justify-between">
                  <span className="text-gray-600 capitalize">{type.replace('-', ' ')}:</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-700 mb-2">Cable Types</h4>
            <div className="space-y-1 text-sm">
              {Object.entries(cableByType).map(([type, count]) => (
                <div key={type} className="flex justify-between">
                  <span className="text-gray-600 capitalize">{type}:</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Report Content - A4 Paper Preview */}
      <div className="flex-1 p-6 overflow-y-auto paper-preview-light" ref={reportRef}>
        {/* Print preview helpers */}
        <style>{`
          .a4-page { background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,0.08); border-radius: 8px; margin: 12px auto; }
          .a4-content { padding: 12mm; }
          .a4-portrait { width: 210mm; min-height: 297mm; }
          .a4-landscape { width: 297mm; min-height: 210mm; }
          .avoid-break { page-break-inside: avoid; }
          @media print {
            .a4-page { box-shadow: none; border-radius: 0; margin: 0; page-break-inside: avoid; }
            .a4-content { padding: 12mm; }
            @page { size: A4 ${orientation}; margin: 12mm; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>
        
        <div className="mx-auto">
          {/* Continuous flowing document with natural page breaks */}
          <div className={`a4-page ${orientation === 'landscape' ? 'a4-landscape' : 'a4-portrait'}`}>
            <div className="a4-content text-sm">
              {/* Header */}
              <div className="border-b border-gray-300 pb-6 mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">Network Infrastructure Report</h1>
                    <p className="text-base text-gray-600">{currentProject.name}</p>
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <p>Generated: {new Date().toLocaleDateString()}</p>
                    <p>Report ID: {currentProject.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <Building size={24} className="mr-2" />
                  Customer Information
                </h2>
                <div className={`${orientation === 'landscape' ? 'grid grid-cols-3 gap-4' : 'grid grid-cols-2 gap-4'}`}>
                  <div>
                    <p className="font-medium text-gray-700">Company:</p>
                    <p className="text-gray-900">{currentProject.customer.name || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Contact Person:</p>
                    <p className="text-gray-900">{customerDetails.contactPerson || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Address:</p>
                    <p className="text-gray-900">{currentProject.customer.address || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Email:</p>
                    <p className="text-gray-900">{currentProject.customer.email || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Phone:</p>
                    <p className="text-gray-900">{currentProject.customer.phone || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Project Date:</p>
                    <p className="text-gray-900">{customerDetails.projectDate}</p>
                  </div>
                </div>
              </div>

              {/* Equipment Inventory */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <Server size={24} className="mr-2" />
                  Equipment Inventory
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Equipment</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Status</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Manufacturer</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Type</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Label</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">IP Address</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">SSID</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">VLAN</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Ports</th>
                      </tr>
                    </thead>
                    <tbody>
                      {design.equipment.map((equipment, index) => (
                        <tr key={equipment.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900">{equipment.equipment.name}</td>
                          <td className="border border-gray-300 px-2 py-1">
                            {equipment.isExisting ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Existing</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">New</span>
                            )}
                          </td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900">{equipment.equipment.manufacturer}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900 capitalize">{equipment.equipment.type.replace('-', ' ')}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900">{equipment.config?.label || '-'}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900">{equipment.config?.ip || '-'}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900">{equipment.config?.ssid || '-'}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900">{equipment.config?.vlan || '-'}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-900">{equipment.equipment.specifications?.ports || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>

          {/* Continue on next page */}
          <div className={`a4-page ${orientation === 'landscape' ? 'a4-landscape' : 'a4-portrait'}`} style={{ pageBreakBefore: 'always' }}>
            <div className="a4-content text-sm">
              {/* Rack Inventory */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <Server size={24} className="mr-2" />
                  Rack Inventory
                </h2>
                <div className="space-y-6">
                  {design.racks.map((rack, index) => (
                    <div key={rack.id} className="bg-gray-50 p-6 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{rack.name}</h3>
                          <p className="text-sm text-gray-600">
                            {rack.size}U Rack • Position: ({Math.round(rack.position.x)}, {Math.round(rack.position.y)})
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {rack.equipment.length} equipment installed
                          </p>
                          <p className="text-sm text-gray-600">
                            {rack.equipment.reduce((total, eq) => total + (eq.equipment.units || 1), 0)}U used / {rack.size}U total
                          </p>
                        </div>
                      </div>
                      
                      {rack.equipment.length > 0 ? (
                        <div className="space-y-3">
                          <h4 className="font-medium text-gray-700 mb-2">Installed Equipment:</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {rack.equipment.map((rackEquipment, eqIndex) => (
                              <div key={`${rack.id}-${eqIndex}`} className="bg-white p-3 rounded border border-gray-200 text-xs">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                    <span className="font-medium text-gray-900">
                                      {rackEquipment.config?.label || rackEquipment.equipment.name}
                                    </span>
                                  </div>
                                  <span className="text-sm text-gray-600">
                                    Unit {rackEquipment.startUnit}-{rackEquipment.startUnit + (rackEquipment.equipment.units || 1) - 1}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600 space-y-1">
                                  <div className="flex justify-between">
                                    <span>Manufacturer:</span>
                                    <span className="font-medium">{rackEquipment.equipment.manufacturer}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Model:</span>
                                    <span className="font-medium">{rackEquipment.equipment.name}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Units:</span>
                                    <span className="font-medium">{rackEquipment.equipment.units || 1}U</span>
                                  </div>
                                  {rackEquipment.equipment.specifications?.ports && (
                                    <div className="flex justify-between">
                                      <span>Ports:</span>
                                      <span className="font-medium">{rackEquipment.equipment.specifications.ports}</span>
                                    </div>
                                  )}
                                  {rackEquipment.config?.label && (
                                    <div className="flex justify-between">
                                      <span>Label:</span>
                                      <span className="font-medium text-blue-600">{rackEquipment.config.label}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <Server size={32} className="mx-auto mb-2 text-gray-400" />
                          <p>No equipment installed in this rack</p>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {design.racks.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Server size={48} className="mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-medium text-gray-700 mb-2">No Racks Configured</h3>
                      <p>Add racks to organize your network equipment</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Cable Connections */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <CableIcon size={24} className="mr-2" />
                  Cable Connections
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Cable ID</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Label</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Status</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Type</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Subtype</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">From</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">To</th>
                        <th className="border border-gray-300 px-2 py-1 text-left font-medium text-gray-700">Length</th>
                      </tr>
                    </thead>
                    <tbody>
                      {designCables.map((cable, index) => {
                        // Parse cable IDs to extract equipment and port information
                        const parseCableId = (cableId: string) => {
                          // Extract port number
                          const portMatch = cableId.match(/:port:(\d+)$/);
                          const port = portMatch ? portMatch[1] : '';
                          
                          // Remove port part to get equipment ID
                          const equipmentId = cableId.replace(/:port:\d+$/, '');
                          
                          // Find equipment in standalone equipment
                          let equipment = currentProject.equipment.find(eq => eq.id === equipmentId);
                          let rackName = '';
                          
                          if (equipment) {
                            return {
                              equipment,
                              rackName: '',
                              port
                            };
                          }
                          
                          // Find equipment in racks
                          for (const rack of currentProject.racks) {
                            const rackEq = rack.equipment.find(eq => eq.equipment.id === equipmentId);
                            if (rackEq) {
                              return {
                                equipment: {
                                  id: rackEq.equipment.id,
                                  equipment: rackEq.equipment,
                                  config: rackEq.config,
                                  position: { x: 0, y: 0 }
                                } as any,
                                rackName: rack.name,
                                port
                              };
                            }
                          }
                          
                          return null;
                        };

                        const fromInfo = parseCableId(cable.from);
                        const toInfo = parseCableId(cable.to);

                        const fromDisplay = fromInfo 
                          ? `${fromInfo.rackName ? `${fromInfo.rackName}: ` : ''}${fromInfo.equipment.config?.label || fromInfo.equipment.equipment.name}${fromInfo.port ? `: Port ${fromInfo.port}` : ''}`
                          : `Unknown Equipment: ${cable.from}`;
                        const toDisplay = toInfo 
                          ? `${toInfo.rackName ? `${toInfo.rackName}: ` : ''}${toInfo.equipment.config?.label || toInfo.equipment.equipment.name}${toInfo.port ? `: Port ${toInfo.port}` : ''}`
                          : `Unknown Equipment: ${cable.to}`;
                        
                        return (
                          <tr key={cable.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="border border-gray-300 px-2 py-1 text-gray-900">{cable.id.slice(0, 8)}</td>
                            <td className="border border-gray-300 px-2 py-1 text-gray-900">{cable.label || '-'}</td>
                            <td className="border border-gray-300 px-2 py-1">
                              {(cable as any).isExisting ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Existing</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">New</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-2 py-1 text-gray-900 capitalize">{cable.type}</td>
                            <td className="border border-gray-300 px-2 py-1 text-gray-900">{cable.subtype || '-'}</td>
                            <td className="border border-gray-300 px-2 py-1 text-gray-900">{fromDisplay}</td>
                            <td className="border border-gray-300 px-2 py-1 text-gray-900">{toDisplay}</td>
                            <td className="border border-gray-300 px-2 py-1 text-gray-900">{cable.length ? `${cable.length}m` : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>

          {/* Final page */}
          <div className={`a4-page ${orientation === 'landscape' ? 'a4-landscape' : 'a4-portrait'}`} style={{ pageBreakBefore: 'always' }}>
            <div className="a4-content text-sm">
              {/* Network Summary */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <Wifi size={24} className="mr-2" />
                  Network Summary
                </h2>
                <div className={`${orientation === 'landscape' ? 'grid grid-cols-3 gap-4' : 'grid grid-cols-2 gap-4'}`}>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Equipment Summary</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Total Equipment:</span>
                        <span className="font-medium">{equipmentCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Cables:</span>
                        <span className="font-medium">{cableCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Racks:</span>
                        <span className="font-medium">{rackCount}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Port Utilization</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Total Ports:</span>
                        <span className="font-medium">{totalPorts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Used Ports:</span>
                        <span className="font-medium">{usedPorts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Available:</span>
                        <span className="font-medium">{totalPorts - usedPorts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Utilization:</span>
                        <span className="font-medium">{totalPorts > 0 ? `${Math.round((usedPorts / totalPorts) * 100)}%` : '0%'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-300 pt-4 mt-8">
                <div className="flex justify-between items-center text-sm text-gray-500">
                  <p>Generated by NetProBuilder</p>
                  <p>Page 1 of 1</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Details Modal */}
      {showCustomerForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 max-w-[90vw] mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Customer Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={customerDetails.companyName}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={customerDetails.contactPerson}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, contactPerson: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={customerDetails.address}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={customerDetails.city}
                      onChange={(e) => setCustomerDetails(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={customerDetails.state}
                      onChange={(e) => setCustomerDetails(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={customerDetails.email}
                      onChange={(e) => setCustomerDetails(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={customerDetails.phone}
                      onChange={(e) => setCustomerDetails(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Description</label>
                  <textarea
                    value={customerDetails.projectDescription}
                    onChange={(e) => setCustomerDetails(prev => ({ ...prev, projectDescription: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCustomerForm(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Save customer details to the project store
                    updateProjectCustomer({
                      name: customerDetails.companyName,
                      address: customerDetails.address,
                      email: customerDetails.email,
                      phone: customerDetails.phone,
                    });
                    setShowCustomerForm(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportView;